import { computed, inject, nextTick, ref, watch, type ComponentPublicInstance } from 'vue';
import { refDebounced, useTimeoutFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';

import router from '@/common/router';
import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { useInternational } from '@/common/locale';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { useDashboardMenu, useMenu } from '@/common/components/organisms/baseMenu/baseMenu.uses';

import {
  CONTENT_MAX_HEIGHT,
  MENU_SEARCH_CONTENT_COMPONENT,
  MENU_SEARCH_SHARED_DATA_KEY,
  MENU_SEARCH_STATE_KEY,
  TOTAL_EXTRA_HEIGHT,
  WINDOW_MAX_HEIGHT,
} from './menuSearch.define';
import {
  MenuSearchRecentItem,
  MenuSearchFavoriteDashboardItem,
  MenuSearchResultItem,
  MenuSearchResultGroup,
  MenuSearchContentComponent,
  MenuSearchSharedData,
  MenuSearchStateData,
} from './menuSearch.type';
import { buildPathToSubTitleMap, groupMenuSearchResults } from './utils/menuSearch.common.utils';
import {
  filterMenuItemRecursively,
  mergeDashboardDataToMenuInfo,
} from './utils/menuSearch.filter.utils';
import {
  mapRecentDataToMenuItems,
  mapFavoriteDashboardsToMenuItems,
} from './utils/menuSearch.default.utils';
import {
  flattenMenuItems,
  getCustomDashboardsWithoutPlatformAsMenuInfo,
} from './utils/menuSearch.result.utils';

// 모든 컴포넌트에서 공유할 검색 상태 관리
export const useMenuSearchState = () => {
  const searchWord = ref<string>('');
  const debouncedSearchWord = refDebounced(searchWord, 500);
  const menuSearchSelectedItemId = ref<string | null>(null);

  return {
    searchWord,
    debouncedSearchWord,
    menuSearchSelectedItemId,
  };
};

export const useSharedMenuSearchState = (
  providedState?: MenuSearchStateData,
): MenuSearchStateData => {
  if (providedState) {
    return providedState;
  }

  const injectedState = inject(MENU_SEARCH_STATE_KEY);

  if (!injectedState) {
    throw new Error('useSharedMenuSearchState must be used within MenuSearch component');
  }

  return injectedState;
};

export const useSharedMenuSearchData = (
  providedData?: MenuSearchSharedData,
): MenuSearchSharedData => {
  if (providedData) {
    return providedData;
  }

  const injectedData = inject(MENU_SEARCH_SHARED_DATA_KEY);

  if (!injectedData) {
    throw new Error('useSharedMenuSearchData must be used within MenuSearch component');
  }

  return injectedData;
};

export const useMenuSearchFilter = (
  sharedData?: MenuSearchSharedData,
  sharedState?: MenuSearchStateData,
) => {
  const { t } = useInternational();

  const { menuInfo } = useMenu();
  const { dashboardMenuInfo, analysisBoardMenuInfo } = useDashboardMenu(menuInfo.value);
  const { dashboardList } = useSharedMenuSearchData(sharedData);
  const { searchWord, debouncedSearchWord, menuSearchSelectedItemId } =
    useSharedMenuSearchState(sharedState);

  // 검색 결과와 subtitle 맵이 같은 병합 메뉴 트리를 공유하도록 분리한다.
  const mergedMenuInfo = computed<MenuInfo[]>(() =>
    mergeDashboardDataToMenuInfo(
      menuInfo.value,
      dashboardMenuInfo.value,
      analysisBoardMenuInfo.value,
    ),
  );

  const pathToSubTitleMap = computed<Map<string, string>>(() =>
    buildPathToSubTitleMap(mergedMenuInfo.value, t),
  );

  const searchFilteringMenuItems = computed<MenuInfo[]>(() => {
    const trimmedKeyword = debouncedSearchWord.value.trim();
    if (!trimmedKeyword) {
      return [];
    }

    const keyword = trimmedKeyword.toLowerCase();

    return mergedMenuInfo.value
      .map((item) => filterMenuItemRecursively(item, keyword))
      .filter((item): item is MenuInfo => item !== null);
  });

  const groupedSearchResults = computed<MenuSearchResultGroup[]>(() => {
    const customDashboardsWithoutPlatform = getCustomDashboardsWithoutPlatformAsMenuInfo(
      dashboardList.value,
      debouncedSearchWord.value,
    );

    const flattenedItems = flattenMenuItems({
      items: searchFilteringMenuItems.value,
      searchWord: debouncedSearchWord.value,
      context: { isDashboard: false, isRootLevel: true },
      pathToSubTitleMap: pathToSubTitleMap.value,
      customDashboards: customDashboardsWithoutPlatform,
    });

    return groupMenuSearchResults(flattenedItems, t);
  });

  const resetMenuSearch = () => {
    searchWord.value = '';
    menuSearchSelectedItemId.value = null;
  };

  return {
    searchWord,
    debouncedSearchWord,
    searchFilteringMenuItems,
    groupedSearchResults,

    resetMenuSearch,
  };
};

export const useMenuSearchContentComponent = (
  sharedData?: MenuSearchSharedData,
  sharedState?: MenuSearchStateData,
) => {
  const { isFetched: isMenuSearchFetched, isError: isMenuSearchError } =
    useSharedMenuSearchData(sharedData);
  const { searchWord, debouncedSearchWord } = useSharedMenuSearchState(sharedState);
  const { menuSearchRecentItems } = useMenuSearchResult(sharedData);
  const { favoriteDashboardList } = useFavoriteDashboards(sharedData);
  const { groupedSearchResults } = useMenuSearchFilter(sharedData, sharedState);

  const isTypingOver3s = ref(false);

  const { start: startTypingTimer, stop: stopTypingTimer } = useTimeoutFn(
    () => {
      isTypingOver3s.value = true;
    },
    3000,
    { immediate: false },
  );

  // "불안정" 상태를 boolean으로 고정해서 감시
  const isUnstable = computed(() => searchWord.value !== debouncedSearchWord.value);

  const component = computed(() => {
    let componentType: MenuSearchContentComponent = 'EMPTY';

    if (!isMenuSearchFetched.value) {
      componentType = 'LOADING';
    } else if (isMenuSearchError.value) {
      // 하나의 API만 에러인 경우
      if (debouncedSearchWord.value) {
        // 검색어가 있는 경우는 검색 결과를 보여줌
        componentType = groupedSearchResults.value.length > 0 ? 'RESULT' : 'NO_RESULT';
      } else {
        // 검색어 없는 경우
        componentType = 'DEFAULT';
      }
    }
    // 에러가 없는 경우 기존 로직
    else if (isTypingOver3s.value) {
      componentType = 'LOADING';
    } else if (debouncedSearchWord.value) {
      componentType = groupedSearchResults.value.length > 0 ? 'RESULT' : 'NO_RESULT';
    } else if (menuSearchRecentItems.value?.length || favoriteDashboardList.value?.length) {
      componentType = 'DEFAULT';
    } else {
      componentType = 'EMPTY';
    }

    return MENU_SEARCH_CONTENT_COMPONENT[componentType];
  });

  watch(
    isUnstable,
    (unstable) => {
      if (unstable) {
        // 불안정 상태로 '진입'했을 때만 3초 타이머 시작
        isTypingOver3s.value = false;
        stopTypingTimer();
        startTypingTimer();
      } else {
        // 안정화되면 즉시 해제
        stopTypingTimer();
        isTypingOver3s.value = false;
      }
    },
    { immediate: true },
  );

  return { component };
};

export const useMenuSearchResult = (sharedData?: MenuSearchSharedData) => {
  const { t } = useInternational();

  const { menuInfo } = useMenu();
  const { isHistoryMenuError, dashboardList, menuSearchRecentData } =
    useSharedMenuSearchData(sharedData);

  const pathToSubTitleMap = computed<Map<string, string>>(() =>
    buildPathToSubTitleMap(menuInfo.value, t),
  );

  const menuSearchRecentItems = computed<MenuSearchRecentItem[]>(() => {
    if (isHistoryMenuError.value || !menuSearchRecentData.value.length) {
      return [];
    }

    return mapRecentDataToMenuItems({
      t,
      recentData: menuSearchRecentData.value,
      menuInfo: menuInfo.value,
      dashboardList: dashboardList.value || [],
      pathToSubTitleMap: pathToSubTitleMap.value,
    });
  });

  return {
    menuSearchRecentItems,
  };
};

export const useFavoriteDashboards = (sharedData?: MenuSearchSharedData) => {
  const { isDashboardListError, dashboardList } = useSharedMenuSearchData(sharedData);

  const favoriteDashboardList = computed<MenuSearchFavoriteDashboardItem[]>(() => {
    if (isDashboardListError.value || !dashboardList.value.length) {
      return [];
    }

    return mapFavoriteDashboardsToMenuItems(dashboardList.value);
  });

  return {
    favoriteDashboardList,
  };
};

export const useMenuSearchNavigation = (sharedState?: MenuSearchStateData) => {
  const { menuSearchSelectedItemId } = useSharedMenuSearchState(sharedState);

  const isMenuSearchItemSelected = (id: string) => {
    return menuSearchSelectedItemId.value === id;
  };

  const selectMenuSearchItem = (id: string) => {
    menuSearchSelectedItemId.value = id;
  };

  // Default 상태의 모든 아이템 (Recent + Dashboard)
  const getDefaultItems = (
    recentItems: MenuSearchRecentItem[],
    dashboardItems: MenuSearchFavoriteDashboardItem[],
  ) => {
    return [...recentItems, ...dashboardItems];
  };

  // 아이템 리스트에서 현재 선택된 인덱스 찾기
  const findSelectedIndex = (items: Array<{ id: string }>) => {
    if (!menuSearchSelectedItemId.value) return -1;
    return items.findIndex((item) => item.id === menuSearchSelectedItemId.value);
  };

  // 위 방향키 처리
  const handleArrowUp = (items: Array<{ id: string }>) => {
    if (items.length === 0) return;

    const currentIndex = findSelectedIndex(items);

    if (currentIndex <= 0) {
      // 첫 번째 아이템이거나 선택된 아이템이 없으면 마지막 아이템 선택
      menuSearchSelectedItemId.value = items[items.length - 1].id;
    } else {
      // 이전 아이템 선택
      menuSearchSelectedItemId.value = items[currentIndex - 1].id;
    }

    scrollSelectedItemIntoView();
  };

  // 아래 방향키 처리
  const handleArrowDown = (items: Array<{ id: string }>) => {
    if (items.length === 0) return;

    const currentIndex = findSelectedIndex(items);
    const maxIndex = items.length - 1;

    if (currentIndex >= maxIndex) {
      // 마지막 아이템이거나 선택된 아이템이 없으면 첫 번째 아이템 선택
      menuSearchSelectedItemId.value = items[0].id;
    } else {
      // 다음 아이템 선택
      menuSearchSelectedItemId.value = items[currentIndex + 1].id;
    }

    scrollSelectedItemIntoView();
  };

  // Default 상태에서 특별 처리: Recent 마지막 → Dashboard 첫 번째
  const handleArrowDownInDefault = (
    recentItems: MenuSearchRecentItem[],
    dashboardItems: MenuSearchFavoriteDashboardItem[],
  ) => {
    const allItems = getDefaultItems(recentItems, dashboardItems);
    const currentIndex = findSelectedIndex(allItems);
    const recentCount = recentItems.length;
    const dashboardCount = dashboardItems.length;

    if (recentCount === 0 && dashboardCount === 0) {
      return;
    }

    // Recent 마지막 아이템에서 아래 누르면 Dashboard 첫 번째로
    if (currentIndex === recentCount - 1 && dashboardCount > 0) {
      menuSearchSelectedItemId.value = allItems[recentCount].id;
      scrollSelectedItemIntoView();
      return;
    }

    // 일반 아래 방향키 처리
    handleArrowDown(allItems);
  };

  // 선택된 아이템을 뷰포트에 스크롤
  const scrollSelectedItemIntoView = () => {
    nextTick(() => {
      const selectedElement = document.querySelector(
        `[data-menu-search-item-id="${menuSearchSelectedItemId.value}"]`,
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth',
        });
      }
    });
  };

  // 첫 번째 아이템 선택
  const selectFirstItem = (items: Array<{ id: string }>) => {
    if (items.length > 0) {
      menuSearchSelectedItemId.value = items[0].id;
    } else {
      menuSearchSelectedItemId.value = null;
    }
  };

  // Enter 키 처리 - 선택된 아이템으로 이동
  const handleEnter = (
    allItems: Array<MenuSearchRecentItem | MenuSearchFavoriteDashboardItem | MenuSearchResultItem>,
    onNavigate?: () => void,
  ) => {
    if (!menuSearchSelectedItemId.value) return;

    // 선택된 아이템 찾기
    const selectedItem = allItems.find((item) => item.id === menuSearchSelectedItemId.value);

    if (selectedItem && selectedItem.path) {
      router.push(selectedItem.path);
      // 네비게이션 후 콜백 실행 (예: 모달 닫기)
      onNavigate?.();
    }
  };

  return {
    isMenuSearchItemSelected,
    selectMenuSearchItem,
    getDefaultItems,
    findSelectedIndex,
    handleArrowUp,
    handleArrowDown,
    handleArrowDownInDefault,
    selectFirstItem,
    handleEnter,
  };
};

export const useMenuSearchWindowSize = (
  sharedData?: MenuSearchSharedData,
  sharedState?: MenuSearchStateData,
) => {
  const baseMenuStore = useBaseMenuStore();
  const { isShowMenuSearch: isShow } = storeToRefs(baseMenuStore);
  const { debouncedSearchWord } = useMenuSearchFilter(sharedData, sharedState);

  // MenuSearchContent 컴포넌트 ref
  const contentRef = ref<ComponentPublicInstance<{ contentRef: HTMLElement }> | HTMLElement | null>(
    null,
  );

  // content 높이 변경을 감지하기 위한 trigger ref
  const contentHeightTrigger = ref(0);

  // window 높이 계산 (CSS 변수에 설정할 값)
  const windowHeightValue = ref<string>('');

  // content 높이 업데이트를 위한 ResizeObserver
  const resizeObserver = ref<ResizeObserver | null>(null);

  // 실제 DOM 요소 가져오기
  const getContentElement = (): HTMLElement | null => {
    if (!contentRef.value) {
      return null;
    }

    // 컴포넌트 인스턴스인 경우
    if ('contentRef' in contentRef.value && contentRef.value.contentRef) {
      return contentRef.value.contentRef as HTMLElement;
    }

    // 직접 HTMLElement인 경우
    if (contentRef.value instanceof HTMLElement) {
      return contentRef.value;
    }

    return null;
  };

  // window 높이 계산 함수
  const calculateWindowHeight = () => {
    const element = getContentElement();
    if (!element) {
      windowHeightValue.value = '';
      return;
    }

    // 실제 콘텐츠 높이 측정
    let contentHeight = 0;

    // 내부 동적 컴포넌트의 실제 높이 측정
    const firstChild = element.firstElementChild as HTMLElement;
    if (firstChild) {
      // 동적 컴포넌트의 실제 높이를 측정
      contentHeight = firstChild.scrollHeight || firstChild.offsetHeight;
    } else {
      // 자식 요소가 없으면 element 자체의 scrollHeight 사용
      contentHeight = element.scrollHeight;
    }

    // content가 max-height 이상이면 window height는 고정값
    if (contentHeight >= CONTENT_MAX_HEIGHT) {
      windowHeightValue.value = `${WINDOW_MAX_HEIGHT}px`;
    } else {
      // content가 max-height 미만이면 content 높이 + 여백
      const calculatedHeight = contentHeight + TOTAL_EXTRA_HEIGHT;
      windowHeightValue.value = `${calculatedHeight}px`;
    }

    // CSS 변수 업데이트
    updateWindowHeightCSS();
  };

  // CSS 변수 업데이트
  const updateWindowHeightCSS = () => {
    nextTick(() => {
      const windowElement = document.querySelector('.menu-search-window') as HTMLElement;
      if (windowElement && windowHeightValue.value) {
        // top 계산: 브라우저 중간에 위치하도록
        const heightNum = parseInt(windowHeightValue.value, 10);
        if (!isNaN(heightNum)) {
          const topValue = `calc((100vh - ${heightNum}px) / 2)`;
          windowElement.style.setProperty('--menu-search-window-top', topValue);
        }
      }
    });
  };

  // content 높이를 강제로 재계산하는 함수
  const triggerHeightRecalculation = () => {
    nextTick(() => {
      contentHeightTrigger.value += 1;
      calculateWindowHeight();
    });
  };

  // 검색어 변경 시 높이 재계산
  watch(debouncedSearchWord, () => {
    if (isShow.value) {
      triggerHeightRecalculation();
    }
  });

  // window가 열릴 때 content ref를 관찰
  watch(isShow, async (newVal) => {
    if (!newVal) {
      const element = getContentElement();
      if (resizeObserver.value && element) {
        resizeObserver.value.unobserve(element);
        resizeObserver.value = null;
      }
      return;
    }

    // window가 열릴 때 content ref를 관찰
    await nextTick();
    const element = getContentElement();
    if (element && !resizeObserver.value) {
      resizeObserver.value = new ResizeObserver(() => {
        // content 높이 변경 시 재계산
        calculateWindowHeight();
      });
      resizeObserver.value.observe(element);
    }

    // 초기 높이 계산
    calculateWindowHeight();
  });

  // contentRef가 변경될 때도 높이 재계산
  watch(contentRef, () => {
    if (isShow.value) {
      nextTick(() => {
        const element = getContentElement();
        if (element && !resizeObserver.value) {
          resizeObserver.value = new ResizeObserver(() => {
            calculateWindowHeight();
          });
          resizeObserver.value.observe(element);
        }
        calculateWindowHeight();
      });
    }
  });

  return {
    contentRef,
    windowHeightValue,
    resizeObserver,

    calculateWindowHeight,
    getContentElement,
    triggerHeightRecalculation,
  };
};
